import {
  PrintPreview,
  ProductAdded,
  ProductUpdated,
  VintagePrintPreview,
} from '@photosi/expo-photosi-sdk/build/Product';
import { IPreviewSvgModelIn } from './previewSvg';
import { IPreviewCanvasModelIn } from './previewCanvas';
import { CanvasPreview, ProductCategoryCode } from '@photosi/expo-photosi-sdk/build/Product';
import { IPreviewSvg } from '@photosi/expo-photosi-sdk/build/PreviewSvg';
import { IUserImageArea } from '@photosi/expo-photosi-sdk/build/UserImageArea';
import { Orientation, UserImageAdded, UserImageUpdated } from '@photosi/expo-photosi-sdk/build/UserImage';
import { ICustomOption, ICustomOptionValue, ILookup } from '@photosi/expo-photosi-sdk/build/Lookup';
import { IProductModelIn } from './product';
import { CanvasBorderType } from '@photosi/expo-photosi-sdk/build/ImageEditor';
import { IUserImageModelIn } from './userImage';
import { ILookupModelIn, ILookupOptionModelIn } from './lookup';
import { ILookupOption } from '@photosi/expo-photosi-sdk/build/LookupOption';
import { ProductChoice, ProductOption, ProjectAdded, ProjectUpdated } from '@photosi/expo-photosi-sdk/build/Project';
import { IProjectModelIn } from './project';
import { ProductOptionsResponse } from '@photosi/expo-photosi-sdk/build/Project';
import { getToolOptionChoiceTitleI18nKey } from 'src/_shared/utils/getI18nKeys';
import { GROUP_CODES, PRODUCT_FAMILY_CODES } from 'src/constants/code.constants';
import { LookupCodes } from 'src/constants/lookup.constants';
import { IProductOptionModelIn } from './productOption';
import { IProductOptionChoiceIn } from './productOptionChoice';
import { IPreviewPrintModelIn } from './previewPrint';
import { IProductOptionChoiceDetailsIn } from './productOptionChoiceDetails';
import { IPreviewVintageModelIn } from './previewVintage';
import { IUserTextModelIn } from './userText';
import { UserText } from '@photosi/expo-photosi-sdk/build/UserImage';

const INNER_VIEWBOX_FALLBACK_SIZE_SCALE = 0.85;
const INNER_VIEWBOX_FALLBACK_OFFSET = (1 - INNER_VIEWBOX_FALLBACK_SIZE_SCALE) / 2;

const PRINT_FAMILY = [
  ProductCategoryCode.SMALL_SIZE,
  ProductCategoryCode.LARGE_SIZE,
  ProductCategoryCode.SMALL_SIZE_WITH_BORDER,
  ProductCategoryCode.PANORAMIC_SIZE,
];

export const parseProject = (projectEvent: ProjectUpdated | ProjectAdded): IProjectModelIn => {
  return {
    id: projectEvent.id,
    projectId: projectEvent.id,
    productsCount: projectEvent.productsCount,
    name: projectEvent.name,
    estimate: projectEvent.estimate,
    original: projectEvent.original ?? undefined,
    cartId: projectEvent.cart_id ?? undefined,
    hasShareToken: projectEvent.hasShareToken ?? undefined,

    groupTypes:
      projectEvent.productGroups
        .filter(g => Object.values(GROUP_CODES).includes(g.code as GROUP_CODES))
        .map(g => ({
          projectId: projectEvent.id,
          id: g.code,
          ...g,
          code: g.code as GROUP_CODES,
          isPlaceholder: true,
        })) ?? [],

    priceDetails: projectEvent.priceDetails ?? undefined,
  };
};

export const parseProduct = (productEvent: ProductUpdated | ProductAdded): IProductModelIn => {
  const productFamily = productEvent.productFamily ?? PRODUCT_FAMILY_CODES.HOMEDECOR;
  return {
    id: productEvent.id,
    projectId: productEvent.projectId,
    productConfigurationId: productEvent.productConfigurationId,
    groupId: productEvent.groupId ?? undefined,
    groupCode: productEvent.groupCode ?? undefined,
    orderIndex: productEvent.orderIndex ?? '',
    smartSelectionTags: productEvent.smartSelectionTags ?? [],
    price: productEvent.price,
    quantity: productEvent.quantity,
    type: productEvent.type,
    family: productFamily,
    lookups: parseLookups(productEvent),
    preview: parsePreview(productEvent),
    choices: {
      correction: productEvent?.choices?.correction ?? '',
      format: productEvent?.choices?.format ?? '',
      paper: productEvent?.choices?.paper ?? '',
      passepartout: productEvent?.choices?.passepartout ?? '',
    },
    actualFormat: productEvent?.actualFormat ?? undefined,
  };
};

export const parseProductV3 = (productEvent: ProductUpdated | ProductAdded): IProductModelIn => {
  const productFamily = productEvent.productFamily ?? PRODUCT_FAMILY_CODES.HOMEDECOR;
  return {
    id: productEvent.id,
    projectId: productEvent.projectId,
    productConfigurationId: productEvent.productConfigurationId,
    groupId:
      productEvent.groupingInfo.groupId +
      (productEvent.groupingInfo?.additionalKeys?.length > 0
        ? '|' + productEvent.groupingInfo.additionalKeys?.join('|')
        : ''),
    groupCode: productEvent.groupCode ?? undefined,
    groupIdSDK: productEvent.groupingInfo.groupId ?? undefined,
    groupingInfo: productEvent.groupingInfo.additionalKeys ?? undefined,
    orderIndex: productEvent.orderIndex ?? '',
    smartSelectionTags: productEvent.smartSelectionTags ?? [],
    price: productEvent.price,
    quantity: productEvent.quantity,
    type: productEvent.type,
    family: productFamily,
    lookups: parseLookups(productEvent),
    preview: parsePreview(productEvent),
    choices: {
      correction: productEvent?.choices?.correction ?? '',
      format: productEvent?.choices?.format ?? '',
      paper: productEvent?.choices?.paper ?? '',
      passepartout: productEvent?.choices?.passepartout ?? '',
    },
    actualFormat: productEvent?.actualFormat ?? undefined,
  };
};

export const parseLookups = (productEvent: ProductAdded | ProductUpdated): ILookupModelIn[] => {
  const productFamily = productEvent.productFamily ?? PRODUCT_FAMILY_CODES.HOMEDECOR;
  const lookupOptions: ILookupModelIn[] = productEvent.lookups.map((eventLookup: ILookup): ILookupModelIn => {
    return {
      identifier: `${productEvent.id}-${eventLookup.code}`,
      projectId: productEvent.projectId,
      productId: productEvent.id,
      code: eventLookup.code,
      // description: eventLookup.description,
      options: eventLookup.options.map((option: ILookupOption): ILookupOptionModelIn => {
        return {
          identifier: `${productEvent.id}-${eventLookup.code}-${option.id}`,
          id: option.id,
          projectId: productEvent.projectId,
          productId: productEvent.id,
          title: option.title, // @deprecated use titleKey instead
          titleKey: getToolOptionChoiceTitleI18nKey(productFamily, eventLookup.code, option.id),
          formattedPrice: option.formattedPrice,
          previewImageUrl: option.previewImageUrl,
          productConfigurationId: option.productConfigurationId, // @deprecated use value instead
          value: option.productConfigurationId,
          selected: option.selected, // @deprecated us isSelected instead
          isSelected: option.selected,
          lookupCode: eventLookup.code,
        };
      }),
    };
  });

  const customOptions: ILookupModelIn[] = productEvent.customOptions.map(
    (eventCustomOption: ICustomOption): ILookupModelIn => {
      return {
        identifier: `${productEvent.id}-${eventCustomOption.code}`,
        projectId: productEvent.projectId,
        productId: productEvent.id,
        code: eventCustomOption.code,
        // description: eventLookup.description,
        isCustom: true,
        options: eventCustomOption.options.map((option: ICustomOptionValue): ILookupOptionModelIn => {
          return {
            identifier: `${productEvent.id}-${eventCustomOption.code}-${option.id}`,
            id: option.id,
            projectId: productEvent.projectId,
            productId: productEvent.id,
            title: option.title, // @deprecated use titleKey instead
            titleKey: getToolOptionChoiceTitleI18nKey(productFamily, eventCustomOption.code, option.id),
            previewImageUrl: option.previewImageUrl,
            selected: false,
            isSelected: false,
            lookupCode: eventCustomOption.code,
            value: option.value,
            productConfigurationId: option.value,
            isCustom: true,
          };
        }),
      };
    },
  );

  const mergedOptions: ILookupModelIn[] = lookupOptions.concat(customOptions);
  const orderedOptions: ILookupModelIn[] = productEvent.optionsOrder.reduce((acc: ILookupModelIn[], code: string) => {
    const option = mergedOptions.find(option => code === option.code);
    return option ? [...acc, option] : acc;
  }, []);

  return orderedOptions;
};

export const parsePreview = (
  productEvent: ProductAdded | ProductUpdated,
): IPreviewSvgModelIn | IPreviewCanvasModelIn | IPreviewPrintModelIn | IPreviewVintageModelIn => {
  if (productEvent.type === ProductCategoryCode.CANVAS) {
    const previewEvent: CanvasPreview = productEvent.preview as CanvasPreview;
    const innerUserImageArea = {
      ...previewEvent.innerUserImageArea,
      projectId: productEvent.projectId,
      productId: productEvent.id,
    };
    const outerUserImageArea = {
      ...previewEvent.outerUserImageArea,
      projectId: productEvent.projectId,
      productId: productEvent.id,
    };

    const previewCanvas: IPreviewCanvasModelIn = {
      ...previewEvent,
      type: productEvent.type,
      projectId: productEvent.projectId,
      productId: productEvent.id,

      userImageAreas: [innerUserImageArea, outerUserImageArea],
    };
    return previewCanvas;
  } else if (PRINT_FAMILY.includes(productEvent.type as ProductCategoryCode)) {
    const previewEvent: PrintPreview = productEvent.preview as PrintPreview;
    const verticalUserImageArea = {
      ...previewEvent.verticalUserImageArea,
      projectId: productEvent.projectId,
      productId: productEvent.id,
    };
    const horizontalUserImageArea = {
      ...previewEvent.horizontalUserImageArea,
      projectId: productEvent.projectId,
      productId: productEvent.id,
    };

    const previewPrint: IPreviewPrintModelIn = {
      ...previewEvent,
      type: productEvent.type,
      projectId: productEvent.projectId,
      productId: productEvent.id,

      userImageAreas: [verticalUserImageArea, horizontalUserImageArea],
    };
    return previewPrint;
  } else if (productEvent.type === ProductCategoryCode.VINTAGE) {
    const previewEvent: VintagePrintPreview = productEvent.preview as VintagePrintPreview;
    const userImageArea = {
      ...previewEvent.userImageArea,
      projectId: productEvent.projectId,
      productId: productEvent.id,
    };
    const userTextAreas = previewEvent.userTextAreas.map(userTextArea => ({
      ...userTextArea,
      projectId: productEvent.projectId,
      productId: productEvent.id,
    }));

    const previewVintage: IPreviewVintageModelIn = {
      ...previewEvent,
      type: productEvent.type,
      projectId: productEvent.projectId,
      productId: productEvent.id,

      userImageAreas: [userImageArea],
      userTextAreas,
    };
    return previewVintage;
  } else {
    const previewEvent: IPreviewSvg = productEvent.preview as IPreviewSvg;
    const previewSvg: IPreviewSvgModelIn = {
      ...previewEvent,
      type: productEvent.type,
      projectId: productEvent.projectId,
      productId: productEvent.id,

      frameUrl: previewEvent.isBackground ? undefined : previewEvent.url,
      backgroundUrl: previewEvent.isBackground ? previewEvent.url : undefined,
      innerViewBox: previewEvent.innerViewBox ?? {
        x: previewEvent.viewBox.width * INNER_VIEWBOX_FALLBACK_OFFSET,
        y: previewEvent.viewBox.height * INNER_VIEWBOX_FALLBACK_OFFSET,
        width: previewEvent.viewBox.width * INNER_VIEWBOX_FALLBACK_SIZE_SCALE,
        height: previewEvent.viewBox.height * INNER_VIEWBOX_FALLBACK_SIZE_SCALE,
      },
      userImageAreas: previewEvent.userImageAreas.map((eventUserImageArea: IUserImageArea) => {
        return {
          projectId: productEvent.projectId,
          productId: productEvent.id,
          ...eventUserImageArea,
        };
      }),
      additionalElements: 'additionalElements' in previewEvent ? (previewEvent as any).additionalElements : undefined,
    };
    return previewSvg;
  }
};

export const userImageParser = (imageEvent: UserImageAdded | UserImageUpdated): IUserImageModelIn => {
  return {
    id: imageEvent.id,
    projectId: imageEvent.projectId,
    productId: imageEvent.productId,
    userImageAreaId: imageEvent.userImageAreaId,
    originalImageOriginalId: imageEvent.transformations.originalImageOriginalId ?? '',

    qualityAlert: imageEvent.qualityAlert,
    cropAlert: imageEvent.cropAlert,

    url: imageEvent.thumbnailUrl,
    width: imageEvent.thumbnailWidth,
    height: imageEvent.thumbnailHeight,
    orientation: (imageEvent.orientation as Orientation) ?? undefined,

    filter: imageEvent.transformations.filter ?? 'original',
    offsetX: imageEvent.transformations.relativeCropX * -1,
    offsetY: imageEvent.transformations.relativeCropY * -1,
    rotationDeg: imageEvent.transformations.rotationDeg,
    scale: imageEvent.transformations.scales[0].scale,
    minScale: Math.min(imageEvent.transformations.minScale, imageEvent.transformations.minRelativeScale),
    minRelativeScale: imageEvent.transformations.minRelativeScale,
    maxScale: imageEvent.transformations.minScale * 5,
    borderType: (imageEvent.transformations.borderType as CanvasBorderType) ?? undefined,
    brightness: imageEvent.transformations.brightness,
    contrast: imageEvent.transformations.contrast,
    saturation: imageEvent.transformations.saturation,
    isUserEdited: imageEvent.transformations.isUserEdited,
    backgroundColor: imageEvent.transformations.backgroundColor ?? '#FFFFFF',

    unsavedChanges: imageEvent.unsavedChanges,
  };
};

export const userTextsParser = (eventUserText: UserText, projectId: string, productId: string): IUserTextModelIn => {
  return {
    id: eventUserText.id,
    projectId: projectId,
    productId: productId,
    userTextAreaId: eventUserText.id,

    text: eventUserText.text,
    size: eventUserText.size,
    font: eventUserText.font,
    color: eventUserText.color,
    anchor: eventUserText.anchor,
    style: eventUserText.style,
  };
};

export const parseProductsOptions = (
  productOptionsResponses: ProductOptionsResponse[],
  productFamily: string,
): IProductOptionModelIn[] => {
  const productOptionsResponsesFiltered: ProductOptionsResponse[] = productOptionsResponses.filter(
    productOptionsResponse => productOptionsResponse.productFamily === productFamily,
  );

  // select only the options that are present in all productOptionsResponsesFiltered
  const optionCodesIntersection: string[] =
    productOptionsResponsesFiltered[0]?.options
      .filter(option => {
        return productOptionsResponsesFiltered.every(productOptionsResponse =>
          productOptionsResponse.options.find(o => o.code === option.code),
        );
      })
      .map(option => option.code) ?? [];

  return productOptionsResponsesFiltered.reduce(
    (acc: IProductOptionModelIn[], productOptionsResponseFiltered: ProductOptionsResponse) =>
      mergeOptions(
        productFamily,
        acc,
        productOptionsResponseFiltered.productId,
        productOptionsResponseFiltered.options.filter(option => optionCodesIntersection.includes(option.code)),
      ),
    [],
  );
};

const mergeOptions = (
  productFamily: string,
  optionsAcc: IProductOptionModelIn[],
  currentProductId: string,
  currentOptions: ProductOption[],
): IProductOptionModelIn[] => {
  const sameOptions: IProductOptionModelIn[] = optionsAcc.map(optionAcc => {
    const currentOption = currentOptions.find(currentOption => currentOption.code === optionAcc.code);
    return {
      ...optionAcc,
      choices: mergeOptionChoices(
        productFamily,
        optionAcc.code,
        [...(optionAcc.choices ?? [])],
        currentProductId,
        currentOption?.choices ?? [],
      ),
    };
  });

  const otherOptions: IProductOptionModelIn[] = currentOptions
    .filter(currentOption => !optionsAcc.find(optionAcc => optionAcc.code === currentOption.code))
    .map(currentOption => ({
      code: currentOption.code,
      choices: mergeOptionChoices(productFamily, currentOption.code, [], currentProductId, currentOption.choices),
    }));

  return [...sameOptions, ...otherOptions];
};

const mergeOptionChoices = (
  productFamily: string,
  productOptionCode: string,
  choicesAcc: IProductOptionChoiceIn[],
  currentProductId: string,
  currentChoices: ProductChoice[],
): IProductOptionChoiceIn[] => {
  const sameChoices: IProductOptionChoiceIn[] = choicesAcc.map(choiceAcc => {
    const currentChoice = currentChoices.find(choice => choice.code === choiceAcc.productOptionChoiceCode);
    if (currentChoice) {
      const parsedProductOptionChoice = parseProductOptionChoice(
        productFamily,
        productOptionCode,
        currentProductId,
        currentChoice,
      );
      return {
        ...parsedProductOptionChoice,
        price: choiceAcc.price === currentChoice?.price ? choiceAcc.price : undefined,
        selected: (choiceAcc.selected && !!currentChoice?.selected) || false,
        enabled: (choiceAcc.enabled && !!currentChoice?.enabled) || false,
        choiceDetails: mergeChoiceDetails(choiceAcc.choiceDetails, currentChoice?.choiceDetails),
        productConfigurationIdsMap: {
          ...choiceAcc.productConfigurationIdsMap,
          ...parsedProductOptionChoice.productConfigurationIdsMap,
        },
      };
    } else {
      return choiceAcc;
    }
  });

  const otherChoices: IProductOptionChoiceIn[] = currentChoices
    .filter(currentChoice => !choicesAcc.find(choiceAcc => choiceAcc.productOptionChoiceCode === currentChoice.code))
    .map(currentChoice => parseProductOptionChoice(productFamily, productOptionCode, currentProductId, currentChoice));

  return [...sameChoices, ...otherChoices];
};

const mergeChoiceDetails = (
  choiceDetailsAcc: IProductOptionChoiceDetailsIn | undefined = {},
  choiceDetails: ProductChoice['choiceDetails'] | undefined,
): IProductOptionChoiceDetailsIn => {
  return typeof choiceDetails === 'object'
    ? Object.entries(choiceDetailsAcc).reduce((acc, [key, value]) => {
        if (key in choiceDetails && choiceDetails[key]?.toLowerCase() === value?.toLowerCase()) {
          return { ...acc, [key]: value?.toLowerCase() };
        } else {
          return acc;
        }
      }, {})
    : {};
};

const parseProductOptionChoice = (
  productFamily: string,
  productOptionCode: string,
  currentProductId: string,
  choice: ProductChoice,
): IProductOptionChoiceIn => {
  const isCustom = [
    LookupCodes.BORDER_TYPE,
    LookupCodes.PASSEPARTOUT,
    LookupCodes.PAPER_COLOR,
    LookupCodes.TEXT,
  ].includes(productOptionCode as LookupCodes);
  return {
    productFamily: productFamily,
    productOptionCode: productOptionCode,
    productOptionChoiceCode: choice.code,
    previewImageUrl: choice.previewImageUrl,
    hint: choice.hint,
    productConfigurationIdsMap: { [currentProductId]: choice.productConfigurationId },
    selected: choice.selected,
    enabled: choice.enabled,
    price: productOptionCode === 'format' ? undefined : choice.price, //TODO: remove to show price for format lookup
    referencingOtherGroupCode: choice.referencingOtherGroupCode,
    isCustom: isCustom,
    choiceDetails: choice.choiceDetails,
  };
};
